/**
 * TenantConfigPanel — Owner switchboard for per-tenant configuration.
 *
 * Single panel per tenant exposing:
 *   - Per-connector on/off switches (free + premium, with optional preset)
 *   - Infrastructure tier (Standard / Enhanced / Sovereign)
 *   - Company size band + region notes + live user count
 *   - Locale (default + supported) — captured now, i18n rollout follows
 *
 * No pricing math, no feature gates, no caps. The Owner is in control;
 * the system is just persistence + audit.
 *
 * Endpoints:
 *   GET   /api/connector-catalogue
 *   GET   /api/tenants/{id}/configuration
 *   PATCH /api/tenants/{id}/configuration
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Settings2, Server, Globe2, Users, Save, Loader2, CheckCircle2, History } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";

const INFRA_TIERS = [
  { value: "standard", label: "Standard", blurb: "Shared cluster, shared database (today's default)." },
  { value: "enhanced", label: "Enhanced", blurb: "Shared cluster, dedicated database — DB-level isolation." },
  { value: "sovereign", label: "Sovereign", blurb: "Dedicated MongoDB cluster, customer-chosen region." },
];

const SIZE_BANDS = [
  { value: "small", label: "Small", hint: "<100 employees / <£10M revenue" },
  { value: "medium", label: "Medium", hint: "100–1,000 / £10–100M" },
  { value: "large", label: "Large", hint: "1,000–10,000 / £100M–£1B" },
  { value: "enterprise", label: "Enterprise / Multinational", hint: ">10,000 / >£1B" },
];

const LOCALE_OPTIONS = [
  { code: "en-GB", label: "English (UK)" },
  { code: "en-US", label: "English (US)" },
  { code: "fr-FR", label: "French" },
  { code: "de-DE", label: "German" },
  { code: "es-ES", label: "Spanish" },
  { code: "it-IT", label: "Italian" },
  { code: "pt-BR", label: "Portuguese (Brazil)" },
  { code: "nl-NL", label: "Dutch" },
  { code: "ja-JP", label: "Japanese" },
  { code: "zh-CN", label: "Chinese (Simplified)" },
  { code: "ar-SA", label: "Arabic" },
];

const PRESET_OPTIONS = [
  { value: "essentials", label: "Essentials (5 connectors)", count: 5 },
  { value: "standard", label: "Standard (8 connectors)", count: 8 },
  { value: "all", label: "All free connectors", count: null },
];

export function TenantConfigPanel({ tenant, onSaved }) {
  const tenantId = tenant?.tenant_id;
  const tenantName = tenant?.name || tenantId;

  const [catalogue, setCatalogue] = useState([]);
  const [config, setConfig] = useState(null);
  const [userCount, setUserCount] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setErr(null);
    try {
      const [catRes, cfgRes, histRes] = await Promise.all([
        api.get("/connector-catalogue"),
        api.get(`/tenants/${tenantId}/configuration`),
        api.get(`/tenants/${tenantId}/configuration/history?limit=10`).catch(() => ({ data: { items: [] } })),
      ]);
      setCatalogue(catRes.data?.items || []);
      setConfig(cfgRes.data?.configuration || null);
      setUserCount(cfgRes.data?.user_count ?? null);
      setHistory(histRes.data?.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load configuration");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const freeConnectors = useMemo(
    () => catalogue.filter((c) => !c.is_premium),
    [catalogue],
  );
  const premiumConnectors = useMemo(
    () => catalogue.filter((c) => c.is_premium),
    [catalogue],
  );

  // null === "all" = unrestricted (defaults). Anything else is an explicit allow-list.
  const enabledSet = useMemo(() => {
    if (!config || config.enabled_connectors === null || config.enabled_connectors === undefined) {
      return null; // "all available"
    }
    return new Set(config.enabled_connectors);
  }, [config]);

  const isOn = (code) => (enabledSet === null ? true : enabledSet.has(code));

  const toggleConnector = (code) => {
    setConfig((prev) => {
      const current = enabledSet === null ? new Set(catalogue.map((c) => c.code)) : new Set(prev.enabled_connectors || []);
      if (current.has(code)) current.delete(code);
      else current.add(code);
      return { ...prev, enabled_connectors: Array.from(current).sort() };
    });
  };

  const applyPreset = (preset) => {
    if (preset === "all") {
      setConfig((prev) => ({ ...prev, enabled_connectors: null }));
      return;
    }
    const codes = freeConnectors.map((c) => c.code);
    const take = preset === "essentials" ? 5 : 8;
    setConfig((prev) => ({ ...prev, enabled_connectors: codes.slice(0, take) }));
  };

  const save = async () => {
    if (!config) return;
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      const r = await api.patch(`/tenants/${tenantId}/configuration`, {
        enabled_connectors: config.enabled_connectors,
        infrastructure_tier: config.infrastructure_tier,
        company_size_band: config.company_size_band || null,
        region_notes: config.region_notes || "",
        default_locale: config.default_locale,
        supported_locales: config.supported_locales,
        peer_benchmarking_enabled: !!config.peer_benchmarking_enabled,
        industry_peer_median_enabled: !!config.industry_peer_median_enabled,
      });
      setConfig(r.data.configuration);
      setOk("Configuration saved.");
      onSaved?.(r.data.configuration);
      // Refresh history so the just-saved change appears immediately.
      api.get(`/tenants/${tenantId}/configuration/history?limit=10`)
        .then((h) => setHistory(h.data?.items || []))
        .catch(() => {});
      setTimeout(() => setOk(null), 3500);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!tenantId) return null;
  if (loading || !config) {
    return (
      <section
        className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
        data-testid="tenant-config-panel"
      >
        <p className="inline-flex items-center gap-2 text-sm text-ek-ink/60">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading {tenantName} configuration…
        </p>
      </section>
    );
  }

  return (
    <section
      className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
      data-testid="tenant-config-panel"
      data-tenant-id={tenantId}
    >
      <div className="flex items-start gap-3">
        <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-ek-purple/10 text-ek-purple">
          <Settings2 className="h-4 w-4" />
        </span>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            Configure {tenantName}
          </h2>
          <p className="mt-1 text-sm text-ek-ink/70">
            Switch connectors on or off, set the infrastructure tier, and
            capture the customer&apos;s size and locale. The system stores
            <em> intent</em> — no caps, no feature gates, no pricing math.
          </p>
        </div>
      </div>

      {/* ----- Connectors ----- */}
      <div className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ek-ink/70">
            Connectors
          </h3>
          <div className="flex items-center gap-2 text-xs">
            <label className="text-ek-ink/60" htmlFor="preset-select">
              Quick-apply preset:
            </label>
            <select
              id="preset-select"
              className="rounded-md border border-black/10 bg-white px-2 py-1 text-xs"
              defaultValue=""
              onChange={(e) => {
                if (!e.target.value) return;
                applyPreset(e.target.value);
                e.target.value = "";
              }}
              data-testid="tenant-config-preset"
            >
              <option value="">— pick —</option>
              {PRESET_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        <ConnectorGrid
          title="Free connectors"
          items={freeConnectors}
          isOn={isOn}
          toggle={toggleConnector}
          testIdPrefix="tenant-config-free-connector"
        />
        <ConnectorGrid
          title="Premium connectors — customer pays the licence directly"
          items={premiumConnectors}
          isOn={isOn}
          toggle={toggleConnector}
          testIdPrefix="tenant-config-premium-connector"
          premium
        />
      </div>

      {/* ----- Infrastructure tier ----- */}
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ek-ink/70 flex items-center gap-2">
            <Server className="h-3.5 w-3.5" /> Infrastructure tier
          </h3>
          <p className="mt-1 text-xs text-ek-ink/60">
            Routes the tenant&apos;s data through the appropriate isolation tier.
          </p>
          <div className="mt-3 space-y-2">
            {INFRA_TIERS.map((t) => (
              <label
                key={t.value}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${
                  config.infrastructure_tier === t.value
                    ? "border-ek-purple bg-ek-purple/5"
                    : "border-black/10 hover:border-black/20"
                }`}
                data-testid={`tenant-config-infra-${t.value}`}
              >
                <input
                  type="radio"
                  name="infra-tier"
                  className="mt-0.5"
                  checked={config.infrastructure_tier === t.value}
                  onChange={() => setConfig({ ...config, infrastructure_tier: t.value })}
                />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-ek-ink">{t.label}</span>
                  <span className="block text-xs text-ek-ink/60">{t.blurb}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* ----- Company size + region notes ----- */}
        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ek-ink/70 flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Tenant context
          </h3>
          <div className="mt-3 rounded-lg border border-black/10 bg-ek-ink/[0.02] p-3 text-sm">
            <p className="text-ek-ink/70">
              Live user count:{" "}
              <span
                className="font-semibold text-ek-ink"
                data-testid="tenant-config-user-count"
              >
                {userCount ?? "—"}
              </span>
            </p>
          </div>
          <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-ek-ink/60">
            Company size band
          </label>
          <select
            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
            value={config.company_size_band || ""}
            onChange={(e) =>
              setConfig({ ...config, company_size_band: e.target.value || null })
            }
            data-testid="tenant-config-size-band"
          >
            <option value="">— not set —</option>
            {SIZE_BANDS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label} ({b.hint})
              </option>
            ))}
          </select>
          <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-ek-ink/60">
            Region notes (free text)
          </label>
          <input
            type="text"
            className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
            placeholder="e.g. UK + Ireland + Singapore"
            value={config.region_notes || ""}
            maxLength={500}
            onChange={(e) => setConfig({ ...config, region_notes: e.target.value })}
            data-testid="tenant-config-region-notes"
          />
        </div>
      </div>

      {/* ----- Locale ----- */}
      <div className="mt-8">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ek-ink/70 flex items-center gap-2">
          <Globe2 className="h-3.5 w-3.5" /> Locale (multilingual support)
        </h3>
        <p className="mt-1 text-xs text-ek-ink/60">
          Captured now; UI translations, locale-aware Board Pack PDFs and
          multi-language voice briefings roll out in a follow-up batch.
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-ek-ink/60">
              Default locale
            </label>
            <select
              className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
              value={config.default_locale}
              onChange={(e) => setConfig({ ...config, default_locale: e.target.value })}
              data-testid="tenant-config-default-locale"
            >
              {LOCALE_OPTIONS.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label} — {l.code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-ek-ink/60">
              Supported locales (multi-select)
            </label>
            <div
              className="mt-1 flex max-h-32 flex-wrap gap-1.5 overflow-y-auto rounded-md border border-black/10 bg-white p-2"
              data-testid="tenant-config-supported-locales"
            >
              {LOCALE_OPTIONS.map((l) => {
                const on = config.supported_locales?.includes(l.code);
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => {
                      const set = new Set(config.supported_locales || []);
                      if (set.has(l.code)) set.delete(l.code);
                      else set.add(l.code);
                      if (set.size === 0) set.add(config.default_locale);
                      setConfig({ ...config, supported_locales: Array.from(set) });
                    }}
                    className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition ${
                      on
                        ? "bg-ek-purple text-white"
                        : "bg-ek-ink/5 text-ek-ink/60 hover:bg-ek-ink/10"
                    }`}
                  >
                    {l.code}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ----- Peer benchmarking toggle (iter161c) ----- */}
      <div className="mt-6 rounded-2xl border border-ek-ink/10 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm font-semibold text-ek-ink">Peer benchmarking</p>
            <p className="mt-1 text-xs text-ek-ink/60">
              Includes a one-sentence peer-median comparison in Maturity radar
              emails (&ldquo;you are +0.6 above the median&rdquo;). Disable for customers
              who consider peer ranking politically sensitive.
            </p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={!!config.peer_benchmarking_enabled}
              onChange={(e) =>
                setConfig({ ...config, peer_benchmarking_enabled: e.target.checked })
              }
              className="h-4 w-4 accent-ek-purple"
              data-testid="tenant-config-peer-benchmarking-toggle"
            />
            <span className="text-xs font-semibold text-ek-ink">
              {config.peer_benchmarking_enabled ? "On" : "Off"}
            </span>
          </label>
        </div>

        {/* iter161e — Industry peer median (sub-toggle, only meaningful
            when peer benchmarking itself is ON). */}
        <div className={`mt-4 flex items-start justify-between gap-4 border-t border-ek-ink/5 pt-4 ${
          !config.peer_benchmarking_enabled ? "opacity-40 pointer-events-none" : ""
        }`}>
          <div className="flex-1">
            <p className="text-sm font-semibold text-ek-ink">Industry-specific median</p>
            <p className="mt-1 text-xs text-ek-ink/60">
              Anchors the comparison against the customer&apos;s industry cohort
              (e.g. &ldquo;Peer median for Financial Services: 2.7&rdquo;) instead of the
              global 3.0. Industries with fewer than 30 cohort scorecards
              automatically fall back to the global median.
            </p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={!!config.industry_peer_median_enabled}
              onChange={(e) =>
                setConfig({ ...config, industry_peer_median_enabled: e.target.checked })
              }
              className="h-4 w-4 accent-ek-purple"
              data-testid="tenant-config-industry-peer-median-toggle"
            />
            <span className="text-xs font-semibold text-ek-ink">
              {config.industry_peer_median_enabled ? "On" : "Off"}
            </span>
          </label>
        </div>
      </div>

      {/* ----- Configuration timeline ----- */}
      <ConfigurationTimeline history={history} />

      {/* ----- Footer ----- */}
      <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
        {err && (
          <span className="text-sm text-red-600" data-testid="tenant-config-error">
            {err}
          </span>
        )}
        {ok && (
          <span
            className="inline-flex items-center gap-1.5 text-sm text-emerald-700"
            data-testid="tenant-config-ok"
          >
            <CheckCircle2 className="h-4 w-4" /> {ok}
          </span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-ek-purple px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-ek-purple/90 disabled:opacity-50"
          data-testid="tenant-config-save"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save configuration
        </button>
      </div>
    </section>
  );
}


function ConnectorGrid({ title, items, isOn, toggle, testIdPrefix, premium }) {
  if (!items.length) return null;
  return (
    <div className="mt-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-ek-ink/60">
        {title}
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => {
          const on = isOn(c.code);
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => toggle(c.code)}
              className={`flex items-start justify-between gap-2 rounded-lg border px-3 py-2 text-left transition ${
                on
                  ? "border-ek-purple/30 bg-ek-purple/[0.04]"
                  : "border-black/10 bg-white hover:border-black/20"
              }`}
              data-testid={`${testIdPrefix}-${c.code}`}
            >
              <span className="flex-1">
                <span className="block text-sm font-semibold text-ek-ink">{c.name}</span>
                <span className="block text-[11px] text-ek-ink/60">
                  {c.category} {c.region ? ` · ${c.region}` : ""}
                </span>
                {premium && (
                  <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                    Customer licence required
                  </span>
                )}
              </span>
              <span
                className={`mt-0.5 inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition ${
                  on ? "bg-ek-purple" : "bg-ek-ink/15"
                }`}
                aria-hidden="true"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
                    on ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// ConfigurationTimeline — surfaces the audit-log entries we already write
// each time the configuration is changed. Read-only artefact for renewal
// conversations: "what's been changed on our tenancy?" → one-screen answer.
// ---------------------------------------------------------------------------
const FIELD_LABEL = {
  enabled_connectors: "Connectors",
  infrastructure_tier: "Infrastructure tier",
  company_size_band: "Company size",
  region_notes: "Region notes",
  default_locale: "Default locale",
  supported_locales: "Supported locales",
  peer_benchmarking_enabled: "Peer benchmarking",
  industry_peer_median_enabled: "Industry-specific peer median",
};

function formatChangeValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) {
    if (value.length === 0) return "(empty)";
    if (value.length > 6) return `${value.slice(0, 6).join(", ")} +${value.length - 6}`;
    return value.join(", ");
  }
  return String(value);
}

function formatAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ConfigurationTimeline({ history }) {
  return (
    <div className="mt-8" data-testid="tenant-config-timeline">
      <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ek-ink/70 flex items-center gap-2">
        <History className="h-3.5 w-3.5" /> Recent configuration changes
      </h3>
      <p className="mt-1 text-xs text-ek-ink/60">
        Read-only audit trail — useful when a customer asks &ldquo;what&apos;s been changed on our tenancy?&rdquo;
      </p>

      {history.length === 0 ? (
        <p
          className="mt-3 rounded-lg border border-dashed border-black/10 bg-ek-ink/[0.02] p-4 text-sm text-ek-ink/55"
          data-testid="tenant-config-timeline-empty"
        >
          No configuration changes recorded yet for this tenant.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {history.map((entry, idx) => (
            <li
              key={`${entry.at}-${idx}`}
              className="rounded-lg border border-black/5 bg-ek-ink/[0.02] p-3"
              data-testid={`tenant-config-timeline-entry-${idx}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
                <span className="font-mono text-ek-ink/55">{formatAt(entry.at)}</span>
                <span className="text-ek-ink/70">
                  by{" "}
                  <span className="font-medium text-ek-ink">
                    {entry.actor_email || entry.actor_user_id || "system"}
                  </span>
                </span>
              </div>
              {entry.changes.length === 0 ? (
                <p className="mt-1 text-xs text-ek-ink/55">
                  Saved with no field changes (no-op).
                </p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {entry.changes.map((c) => (
                    <li
                      key={c.field}
                      className="text-xs text-ek-ink/80"
                      data-testid={`tenant-config-timeline-change-${c.field}`}
                    >
                      <span className="font-semibold text-ek-ink">
                        {FIELD_LABEL[c.field] || c.field}:
                      </span>{" "}
                      <span className="text-ek-ink/55 line-through decoration-ek-ink/30">
                        {formatChangeValue(c.from)}
                      </span>
                      <span className="mx-1.5 text-ek-ink/40">→</span>
                      <span className="text-ek-purple">{formatChangeValue(c.to)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


export default TenantConfigPanel;